import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
// Served from /public so it has a stable URL preloaded in index.html
const tsxLogo = "/tsx-logo.webp";
import CartDrawer from "./CartDrawer";
import ProductsMegaMenu from "./ProductsMegaMenu";
import UserMenu from "@/components/account/UserMenu";

const ApexHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle navigation to home page sections
  const handleSectionClick = (sectionId: string) => {
    if (location.pathname !== "/") {
      // Navigate to home first, then scroll to section
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      // Already on home, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  };

  const navLinks = [
    { label: "ABOUT", sectionId: "about" },
    { label: "PROCESS", sectionId: "process" },
    { label: "CONTACT", href: "/sign-up", isRoute: true },
    { label: "PROJECTS", href: "/projects", isRoute: true },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-gradient-to-b from-background via-background/95 to-background/90 backdrop-blur-md shadow-lg" 
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src={tsxLogo} 
              alt="TSX" 
              width={200}
              height={70}
              fetchPriority="high"
              decoding="async"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {/* Products Mega Menu - First */}
            <ProductsMegaMenu />
            
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href!}
                  className="text-sm font-medium tracking-widest text-white/90 hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleSectionClick(link.sectionId!)}
                  className="text-sm font-medium tracking-widest text-white/90 hover:text-primary transition-colors"
                >
                  {link.label}
                </button>
              )
            ))}
          </nav>

          {/* User, Cart & Mobile Menu Toggle */}
          <div className="flex items-center gap-3">
            <UserMenu />
            <CartDrawer />
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-white"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-md border-t border-border/50">
          <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
            {/* Mobile Products Menu - First */}
            <ProductsMegaMenu isMobile onItemClick={() => setIsMenuOpen(false)} />
            
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.label}
                  to={link.href!}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-sm font-medium tracking-widest text-foreground hover:text-primary transition-colors py-2"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleSectionClick(link.sectionId!)}
                  className="text-sm font-medium tracking-widest text-foreground hover:text-primary transition-colors py-2 text-left"
                >
                  {link.label}
                </button>
              )
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default ApexHeader;
